import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
} from '@mui/material';
import {
  Star as StarIcon,
  VerifiedUser as VerifiedUserIcon,
  ArrowDownward as ArrowDownwardIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import FinMapleScoreBadge from '../FinMapleScore/FinMapleScoreBadge';
import FreshnessBadge from './FreshnessBadge';
import { useTranslation } from 'react-i18next';
import { parseFee, fmtCurrency, fmtPercent, getRewardType } from './helpers';
import { calculateFinMapleScore } from '../../utils/finmapleScore';

/* ── i18n labels ── */
const L = {
  es: {
    cashback: 'Cash back', points: 'Puntos', miles: 'Millas', rewards: 'Recompensas',
    title: 'Nuestras recomendaciones para ti',
    subtitle: 'Basadas en tu perfil. Seleccionamos las 3 mejores opciones.',
    profileDetected: 'Perfil detectado:',
    profileActive: 'Perfil activo',
    bestOption: 'Mejor opción 2026',
    newcomerRecommended: 'Recomendada para newcomers',
    apply: 'Solicitar',
    applyTime: 'Aplicación en 5 minutos',
    viewMore: 'Ver más opciones',
    repeatQuiz: 'Repetir cuestionario',
    time: {
      new: 'Menos de 6 meses en Canadá',
      '6mo_2yr': '6 meses a 2 años en Canadá',
      '2yr_5yr': '2 a 5 años en Canadá',
      '5yr_plus': 'Más de 5 años en Canadá',
    },
    credit: {
      none: 'Sin historial crediticio',
      building: 'Construyendo historial crediticio',
      established: 'Historial crediticio establecido',
    },
    priority: {
      no_fee: 'Buscas sin cuota anual',
      build_credit: 'Quieres construir crédito',
      cashback: 'Prefieres cash back',
      travel: 'Te interesan viajes y millas',
    },
  },
  en: {
    cashback: 'Cash back', points: 'Points', miles: 'Miles', rewards: 'Rewards',
    title: 'Our recommendations for you',
    subtitle: 'Based on your profile. We selected the top 3 options.',
    profileDetected: 'Profile detected:',
    profileActive: 'Active profile',
    bestOption: 'Best option 2026',
    newcomerRecommended: 'Recommended for newcomers',
    apply: 'Apply',
    applyTime: 'Apply in 5 minutes',
    viewMore: 'View more options',
    repeatQuiz: 'Repeat questionnaire',
    time: {
      new: 'Less than 6 months in Canada',
      '6mo_2yr': '6 months to 2 years in Canada',
      '2yr_5yr': '2 to 5 years in Canada',
      '5yr_plus': 'More than 5 years in Canada',
    },
    credit: {
      none: 'No credit history',
      building: 'Building credit history',
      established: 'Established credit history',
    },
    priority: {
      no_fee: 'Looking for no annual fee',
      build_credit: 'Want to build credit',
      cashback: 'Prefer cash back',
      travel: 'Interested in travel and miles',
    },
  },
  fr: {
    cashback: 'Remises', points: 'Points', miles: 'Milles', rewards: 'Récompenses',
    title: 'Nos recommandations pour vous',
    subtitle: 'Basées sur votre profil. Nous avons sélectionné les 3 meilleures options.',
    profileDetected: 'Profil détecté :',
    profileActive: 'Profil actif',
    bestOption: 'Meilleure option 2026',
    newcomerRecommended: 'Recommandée pour les nouveaux arrivants',
    apply: 'Demander',
    applyTime: 'Demande en 5 minutes',
    viewMore: 'Voir plus d\'options',
    repeatQuiz: 'Refaire le questionnaire',
    time: {
      new: 'Moins de 6 mois au Canada',
      '6mo_2yr': '6 mois à 2 ans au Canada',
      '2yr_5yr': '2 à 5 ans au Canada',
      '5yr_plus': 'Plus de 5 ans au Canada',
    },
    credit: {
      none: 'Aucun historique de crédit',
      building: 'En construction d\'historique de crédit',
      established: 'Historique de crédit établi',
    },
    priority: {
      no_fee: 'Cherche sans frais annuels',
      build_credit: 'Veut bâtir son crédit',
      cashback: 'Préfère les remises en argent',
      travel: 'Intéressé par les voyages et milles',
    },
  },
};

const getScoreColor = (total) => {
  if (total >= 4.5) return '#2E7D32';
  if (total >= 4.0) return '#1E3A5F';
  return '#F9A825';
};

/* ── Rank badge - circular ───────────────────── */
const RankBadge = ({ rank }) => {
  const isFirst = rank === 1;
  return (
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        bgcolor: isFirst ? 'rgba(249,168,37,0.12)' : 'grey.100',
        border: '2px solid',
        borderColor: isFirst ? '#F9A825' : 'divider',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Typography
        sx={{
          fontWeight: 700,
          fontFeatureSettings: "'tnum'",
          fontSize: '0.95rem',
          color: isFirst ? '#F9A825' : 'text.secondary',
          lineHeight: 1,
        }}
      >
        {rank}
      </Typography>
    </Box>
  );
};

const CardImage = ({ card }) => {
  const [error, setError] = useState(false);
  const url = card.image_url;
  const isValidImg = url && !url.includes('.html') && !error;

  if (isValidImg) {
    return (
      <Box
        component="img"
        src={url}
        alt={card.name}
        loading="lazy"
        onError={() => setError(true)}
        sx={{ width: 120, height: 76, objectFit: 'contain', borderRadius: '8px' }}
      />
    );
  }

  const initials = (card.bank || 'CC')
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 3)
    .toUpperCase();

  return (
    <Box
      sx={{
        width: 120,
        height: 76,
        borderRadius: '8px',
        bgcolor: 'primary.main',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '1.2rem' }}>
        {initials}
      </Typography>
    </Box>
  );
};

/* ── Profile summary banner ──────────────────── */
const ProfileSummary = ({ answers, l }) => {
  if (!answers) return null;

  const items = [
    l.time[answers.time_in_canada],
    l.credit[answers.credit_history],
    l.priority[answers.priority],
  ].filter(Boolean);

  if (items.length === 0) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mb: 3,
        borderRadius: '12px',
        bgcolor: '#f8fafc',
        border: '1px solid',
        borderColor: 'rgba(30,58,95,0.12)',
        '@keyframes fadeInProfile': {
          from: { opacity: 0, transform: 'translateY(6px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        animation: 'fadeInProfile 0.3s ease-out',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <CheckCircleIcon sx={{ fontSize: '1rem', color: '#2E7D32' }} />
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, color: 'primary.main' }}
        >
          {l.profileDetected}
        </Typography>
        <Chip
          label={l.profileActive}
          size="small"
          sx={{
            height: 20,
            fontSize: '0.75rem',
            fontWeight: 600,
            bgcolor: 'rgba(46,125,50,0.1)',
            color: '#2E7D32',
            borderRadius: '999px',
          }}
        />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {items.map((item) => (
          <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <CheckCircleIcon sx={{ fontSize: '0.9rem', color: '#2E7D32' }} />
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
              {item}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

/* ── Score badge for top card (larger) ─────── */
const LargeScoreBadge = ({ card }) => {
  const { total } = calculateFinMapleScore(card);
  const color = getScoreColor(total);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
      <Box
        sx={{
          width: 52,
          height: 52,
          borderRadius: '50%',
          border: '3px solid',
          borderColor: color,
          bgcolor: `${color}12`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography sx={{ fontWeight: 700, fontFeatureSettings: "'tnum'", color, fontSize: '1.1rem', lineHeight: 1 }}>
          {total.toFixed(1)}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: '0.75rem', color: '#5F6B7A', fontWeight: 600, mt: 0.5 }}>
        FinMaple Score
      </Typography>
      {/* Mini scale bar */}
      <Box sx={{ width: 40, height: 3, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.06)', mt: 0.3 }}>
        <Box sx={{ width: `${(total / 5) * 100}%`, height: '100%', borderRadius: 2, bgcolor: color, transition: 'width 0.3s ease' }} />
      </Box>
    </Box>
  );
};

const WizardResults = ({ topCards, onViewAll, onRepeatWizard, wizardAnswers }) => {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'es';
  const l = L[lang] || L.es;

  const REWARD_TYPE_LABELS = {
    cashback: l.cashback, points: l.points, miles: l.miles, other: l.rewards, none: null,
  };

  if (!topCards || topCards.length === 0) return null;

  return (
    <Box
      sx={{
        mb: 4,
        '@keyframes slideUp': {
          from: { opacity: 0, transform: 'translateY(12px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        animation: 'slideUp 0.4s ease-out',
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}
        >
          {l.title}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {l.subtitle}
        </Typography>
      </Box>

      {/* Profile summary */}
      <ProfileSummary answers={wizardAnswers} l={l} />

      {/* Top 3 cards */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mb: 3 }}>
        {topCards.map((card, idx) => {
          const fee = parseFee(card.annual_fee);
          const rewardType = getRewardType(card);
          const rewardLabel = REWARD_TYPE_LABELS[rewardType];
          const isTop = idx === 0;

          return (
            <Paper
              key={card.id}
              elevation={0}
              sx={{
                p: { xs: 2.5, md: 3.5 },
                borderRadius: '14px',
                border: isTop ? '2px solid' : '1px solid',
                borderColor: isTop ? 'secondary.main' : 'divider',
                ...(isTop && {
                  borderLeft: '5px solid',
                  borderLeftColor: 'secondary.main',
                  bgcolor: '#FFFBF8',
                  boxShadow: '0 6px 32px rgba(211,47,47,0.12), 0 2px 8px rgba(0,0,0,0.04)',
                }),
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: 2,
                position: 'relative',
                transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                '&:hover': {
                  boxShadow: isTop
                    ? '0 8px 36px rgba(211,47,47,0.16), 0 3px 12px rgba(0,0,0,0.06)'
                    : '0 4px 16px rgba(0,0,0,0.08)',
                  transform: 'translateY(-2px)',
                },
                '@keyframes fadeInCard': {
                  from: { opacity: 0, transform: 'translateY(8px)' },
                  to: { opacity: 1, transform: 'translateY(0)' },
                },
                animation: `fadeInCard 0.3s ease-out ${idx * 0.1}s both`,
              }}
            >
              {/* Top badge */}
              {isTop && (
                <Chip
                  icon={<StarIcon sx={{ fontSize: '0.9rem' }} />}
                  label={l.bestOption}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: -12,
                    left: 16,
                    bgcolor: 'secondary.main',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    height: 28,
                    '& .MuiChip-icon': { color: '#fff' },
                    boxShadow: '0 3px 12px rgba(211,47,47,0.3)',
                  }}
                />
              )}

              {/* Rank badge */}
              <RankBadge rank={idx + 1} />

              {/* Card image */}
              <CardImage card={card} />

              {/* Info */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {card.bank}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>
                  {card.name}
                </Typography>
                {!isTop && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <FinMapleScoreBadge card={card} />
                    {card.v2?.freshness && <FreshnessBadge status={card.v2.freshness} />}
                  </Box>
                )}
                {card.v2?.welcome_bonus && (
                  <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600 }}>
                    {card.v2.welcome_bonus.headline}
                  </Typography>
                )}

                {/* Stats + newcomer tag */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                  <Chip
                    label={fmtCurrency(card.annual_fee, lang)}
                    size="small"
                    sx={{
                      bgcolor: fee === 0 ? '#1E3A5F' : 'action.hover',
                      color: fee === 0 ? '#fff' : 'text.primary',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                    }}
                  />
                  <Chip
                    label={fmtPercent(card.purchase_rate)}
                    size="small"
                    sx={{ bgcolor: 'rgba(0,0,0,0.04)', color: '#5F6B7A', fontWeight: 600, fontSize: '0.875rem' }}
                  />
                  {card.newcomer_friendly && (
                    <Chip
                      icon={<VerifiedUserIcon sx={{ fontSize: '0.875rem' }} />}
                      label={isTop ? l.newcomerRecommended : 'Newcomers'}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(46,125,50,0.08)',
                        color: '#2E7D32',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        '& .MuiChip-icon': { color: '#2E7D32' },
                      }}
                    />
                  )}
                  {rewardLabel && (
                    <Chip
                      label={rewardLabel}
                      size="small"
                      sx={{ bgcolor: 'rgba(249,168,37,0.12)', color: '#7D5A00', fontWeight: 600, fontSize: '0.875rem' }}
                    />
                  )}
                </Box>
              </Box>

              {/* Score (large for top card) */}
              {isTop && <LargeScoreBadge card={card} />}

              {/* CTA */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                <Button
                  variant="contained"
                  href={card.affiliate_link || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    bgcolor: 'secondary.main',
                    color: '#fff',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '10px',
                    px: 4,
                    py: 1.2,
                    whiteSpace: 'nowrap',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'secondary.dark',
                      transform: 'scale(1.03)',
                      boxShadow: '0 4px 16px rgba(211,47,47,0.25)',
                    },
                  }}
                >
                  {l.apply}
                </Button>
                <Typography sx={{ fontSize: '0.75rem', color: '#5F6B7A', mt: 0.5 }}>
                  {l.applyTime}
                </Typography>
              </Box>
            </Paper>
          );
        })}
      </Box>

      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          endIcon={<ArrowDownwardIcon />}
          onClick={onViewAll}
          sx={{
            borderColor: 'primary.main',
            color: 'primary.main',
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '10px',
          }}
        >
          {l.viewMore}
        </Button>
        <Button
          onClick={onRepeatWizard}
          sx={{ color: 'text.secondary', textTransform: 'none' }}
        >
          {l.repeatQuiz}
        </Button>
      </Box>
    </Box>
  );
};

export default WizardResults;
